import { z } from 'zod';

/**
 * The trigger kinds an agent-skill definition may declare support for
 * (IMM-436.1). A definition names a non-empty subset; an instance
 * (IMM-436.2) picks one of them. Tokens are English — the German labels
 * live in `@immo/i18n`.
 */
export const SKILL_TRIGGER_KINDS = ['EVENT', 'SCHEDULE', 'MANUAL'] as const;

export const SkillTriggerKindSchema = z.enum(SKILL_TRIGGER_KINDS);

export type SkillTriggerKind = (typeof SKILL_TRIGGER_KINDS)[number];

export const AgentSkillDefinitionStatusSchema = z.enum(['active', 'inactive']);

export type AgentSkillDefinitionStatus = z.infer<typeof AgentSkillDefinitionStatusSchema>;

/**
 * Field bounds, exported so both the domain value objects and the operator
 * form's helper copy derive from one constant instead of restating it
 * (a unit or range stated twice drifts — see bug-learnings BL-022).
 */
export const AGENT_SKILL_NAME_MAX_LENGTH = 60;
export const AGENT_SKILL_DESCRIPTION_MAX_LENGTH = 500;
/** Matches the managed-prompt bound IMM-406.3 established for `skills.prompt`. */
export const MAX_AGENT_SKILL_INSTRUCTIONS_LENGTH = 10_000;
export const AGENT_SKILL_APPROVAL_WINDOW_MIN_DAYS = 1;
export const AGENT_SKILL_APPROVAL_WINDOW_MAX_DAYS = 7;
export const AGENT_SKILL_APPROVAL_WINDOW_DEFAULT_DAYS = 7;

/** A definition's immutable Key: a lower-case slug, unique within the family. */
export const AGENT_SKILL_KEY_PATTERN = /^[a-z][a-z0-9]*(?:[-_][a-z0-9]+)*$/;
export const AGENT_SKILL_KEY_MAX_LENGTH = 60;

/**
 * One row of the admin Agent-Skills list. Carries the tokens only — the German
 * status and trigger labels are resolved on the frontend from `@immo/i18n`.
 */
export const AgentSkillDefinitionSummarySchema = z.object({
  key: z.string().min(1),
  name: z.string().min(1),
  description: z.string().min(1),
  status: AgentSkillDefinitionStatusSchema,
  supportedTriggers: z.array(SkillTriggerKindSchema),
  lastEditedByName: z.string().min(1).nullable(),
  lastEditedAt: z.iso.datetime(),
});

export type AgentSkillDefinitionSummary = z.infer<typeof AgentSkillDefinitionSummarySchema>;

export const ListAgentSkillDefinitionsResponseSchema = z.object({
  data: z.array(AgentSkillDefinitionSummarySchema),
});

export type ListAgentSkillDefinitionsResponse = z.infer<typeof ListAgentSkillDefinitionsResponseSchema>;

/**
 * The editor view of one definition. `version` is the optimistic-concurrency
 * token the client echoes back on every write.
 */
export const AgentSkillDefinitionViewSchema = AgentSkillDefinitionSummarySchema.extend({
  instructions: z.string().min(1),
  defaultAllowlist: z.array(z.string().min(1)),
  approvalWindowDays: z.number().int(),
  version: z.number().int(),
});

export type AgentSkillDefinitionView = z.infer<typeof AgentSkillDefinitionViewSchema>;

export const AgentSkillDefinitionPathParamsSchema = z.object({
  key: z.string().min(1),
});

/**
 * The editable fields, shared by create and update. Deliberately permissive:
 * every bound is enforced by the domain value objects so a refusal carries its
 * own stable `admin.agent-skill.*` code and the operator sees a field-specific
 * message, rather than a generic zod 400. Same shape IMM-406.3 uses for
 * `SaveDocumentAnalysisSkillPromptBodySchema`.
 */
const AgentSkillDefinitionFieldsSchema = z.object({
  name: z.string(),
  description: z.string(),
  instructions: z.string(),
  supportedTriggers: z.array(z.string()),
  defaultAllowlist: z.array(z.string()),
  // null selects the default window (7 days).
  approvalWindowDays: z.number().int().nullable(),
});

export const CreateAgentSkillDefinitionBodySchema = AgentSkillDefinitionFieldsSchema.extend({
  // Immutable once created — absent from the update body by design.
  key: z.string(),
});

export type CreateAgentSkillDefinitionBody = z.infer<typeof CreateAgentSkillDefinitionBodySchema>;

export const UpdateAgentSkillDefinitionBodySchema = AgentSkillDefinitionFieldsSchema.extend({
  expectedVersion: z.number().int(),
});

export type UpdateAgentSkillDefinitionBody = z.infer<typeof UpdateAgentSkillDefinitionBodySchema>;

export const SetAgentSkillDefinitionStatusBodySchema = z.object({
  expectedVersion: z.number().int(),
});

export type SetAgentSkillDefinitionStatusBody = z.infer<typeof SetAgentSkillDefinitionStatusBodySchema>;
